import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/utils/formatCurrency";
import { getStoredTransaction } from "@/utils/getStoredUser";
import {
  useCreateTransaction,
  useUpdateTransaction,
} from "../hooks/useCreateTransaction";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  useTransactionStore,
  selectGrandTotal,
} from "@/features/Cart/store/transactionStore";
import { formatOutDate } from "@/utils/formatOutDate";

export default function PaymentBase() {
  const presetAmounts = [2000, 5000, 10000, 20000, 50000, 100000];
  const navigate = useNavigate();

  // Ambil data dari store
  const paidAmount = useTransactionStore((state) => state.paidAmount);
  const setPaidAmount = useTransactionStore((state) => state.setPaidAmount);
  const clearTransaction = useTransactionStore(
    (state) => state.clearTransaction,
  );

  // Gunakan selector untuk mendapatkan total yang sudah dihitung dengan benar
  const totalBelanja = useTransactionStore(selectGrandTotal);

  // Baca metode pembayaran dari localStorage langsung agar pasti sinkron
  const storedData = getStoredTransaction();
  const metodePembayaran = storedData?.metodePembayaran || "";
  const isCash = metodePembayaran == "Cash";

  // Hitung kembalian
  const change = paidAmount - totalBelanja;

  // State untuk tracking auto-submit non-cash
  const [isProcessing, setIsProcessing] = useState(!isCash);

  const { mutate: createTransaction, isPending } = useCreateTransaction({
    onSuccess: (data) => {
      clearTransaction();
      setTimeout(() => {
        navigate(`/success/${data.data?.out_no}`, { replace: true });
      }, 1000);
    },
    onError: (error) => {
      toast.error("  gagal disimpan: " + error.message);
      console.error("Error:", error);
      setIsProcessing(false);
    },
  });

  const { mutate: updateTransaction, isPending: isUpdating } =
    useUpdateTransaction({
      onSuccess: (data) => {
        clearTransaction();
        setTimeout(() => {
          navigate(`/success/${data.data?.out_no}`, { replace: true });
        }, 1000);
      },
      onError: (error) => {
        toast.error("  gagal disimpan: " + error.message);
        setIsProcessing(false);
      },
    });

  const buildPayload = (overrideTotalBayar?: number) => {
    const data = getStoredTransaction();

    const paymentChannelMap: Record<string, number> = {
      Cash: 1,
      Transfer: 2,
      QRIS: 3,
    };

    return {
      payload: {
        out_no: data.out_no,
        out_date: formatOutDate(data.tanggalTransaksi),
        cust_id: Number(data.customer),
        totalBayar: overrideTotalBayar ?? data.paidAmount,
        channelId: paymentChannelMap[data.metodePembayaran] || 0,
        payment_status: 1,
        is_pending: 0,
        diskon_total: data.diskonSubtotal, // rupiah
        ppn: data.ppn, // rupiah
        items:
          data?.cart?.map((item: any) => ({
            mtrl_code: item.mtrl_code,
            qty: item.qty,
            diskon: item.discount || 0, // rupiah
          })) || [],
      },
      outNo: data.out_no,
    };
  };

  // Untuk non-cash: otomatis submit saat halaman dibuka
  const hasAutoSubmitted = useRef(false);
  useEffect(() => {
    if (!isCash && !hasAutoSubmitted.current) {
      hasAutoSubmitted.current = true;
      const { payload, outNo } = buildPayload(totalBelanja);

      if (outNo) {
        updateTransaction(payload);
      } else {
        createTransaction(payload);
      }
    }
  }, []);

  const handlePay = async () => {
    const { payload, outNo } = buildPayload();

    if (outNo) {
      await updateTransaction(payload);
    } else {
      await createTransaction(payload);
    }
  };

  const handlePresetClick = (amount: number) => {
    setPaidAmount(paidAmount + amount);
  };

  const handleManualInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    const numericValue = value === "" ? 0 : Number(value);
    setPaidAmount(numericValue);
  };

  const handleReset = () => {
    setPaidAmount(0);
  };

  return !isCash ? (
    // Non-cash: loading screen
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="animate-spin w-12 h-12 border-4 border-[#d7d0fe] border-t-[#7c3aed] rounded-full mx-auto" />
        <p className="text-lg font-semibold text-[#1e1e1e] dark:text-white">
          Memproses pembayaran {metodePembayaran}...
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Total: {formatCurrency(String(totalBelanja))}
        </p>
      </div>
    </div>
  ) : (
    // Cash: UI lengkap
    <div className="min-h-screen">
      <div className="w-full overflow-hidden">
        <div className="p-6 space-y-6">
          {/* Total Belanja */}
          <div className="bg-[#efecfa] dark:bg-[#37393d] p-5 rounded-xl text-center">
            <p className="text-sm text-gray-600 dark:text-white">
              Total Belanja
            </p>
            <button
              className="transition-all duration-300 cursor-pointer hover:scale-105"
              onClick={() => handlePresetClick(totalBelanja)}
            >
              <p className="text-3xl font-bold text-[#1e1e1e] dark:text-white">
                {formatCurrency(String(totalBelanja))}
              </p>
            </button>
          </div>

          <div className="w-full h-px bg-gray-300" />

          {/* Preset */}
          <div className="grid grid-cols-3 gap-3">
            {presetAmounts?.map((amount) => (
              <Button
                key={amount}
                variant="outline"
                className="h-12 font-medium bg-[#efecff] border-[#efecff] hover:bg-[#d7d0fe]"
                onClick={() => handlePresetClick(amount)}
              >
                {formatCurrency(String(amount))}
              </Button>
            ))}
          </div>

          {/* Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#37393d] dark:text-white">
              Input Manual (Rp)
            </label>
            <Input
              type="text"
              value={formatCurrency(String(paidAmount))}
              onChange={handleManualInput}
              placeholder="0"
              className="text-lg font-medium text-center h-14"
            />
          </div>

          {/* Kembalian */}
          <div
            className={`rounded-xl p-5 text-center ${
              change >= 0
                ? "bg-green-50 dark:bg-[#37393d]"
                : "bg-gray-100 dark:bg-[#37393d]"
            }`}
          >
            <p className="text-sm text-gray-600 dark:text-white">Kembalian</p>
            <p
              className={`text-3xl font-bold ${
                change >= 0
                  ? "text-green-700 dark:text-green-300"
                  : "text-[#37393d] dark:text-white"
              }`}
            >
              {formatCurrency(String(Math.max(0, change)))}
            </p>

            {change < 0 && (
              <p className="text-sm text-[#ff7075] mt-2 font-medium">
                Kurang {formatCurrency(String(Math.abs(change)))}
              </p>
            )}
          </div>

          {/* Bayar */}
          <Button
            onClick={handlePay}
            disabled={paidAmount < totalBelanja || isPending}
            className={`w-full h-14 text-lg font-bold ${
              paidAmount >= totalBelanja
                ? "bg-green-600 hover:bg-green-600/90 text-white"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {isPending ? "Memproses..." : "Bayar"}
          </Button>

          {/* Reset */}
          <Button
            onClick={handleReset}
            className="w-full h-14 text-lg font-bold bg-[#ff7075] hover:bg-[#ff7075]/90 text-white"
          >
            Reset
          </Button>
        </div>
      </div>
    </div>
  );
}
